import { supabase } from './supabase';

export interface CoachingClient {
  id: string;
  coach_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  user_id: string | null;
  status: string;
  goals: string | null;
  notes: string | null;
  created_at: string;
}

export type AnalysisStatus = 'idle' | 'processing' | 'complete' | 'error';
export type ConfidenceLevel = 'low' | 'medium' | 'high';
export type SeverityLevel = 'mild' | 'notable' | 'significant';
export type InputType = 'audio_transcript' | 'transcript' | 'notes_only';

export interface KeyTheme {
  theme: string;
  evidence: string;
  possible_importance: string;
  confidence: ConfidenceLevel;
}

export interface LimitingBelief {
  client_quote: string;
  possible_belief: string;
  severity: SeverityLevel;
  coaching_exploration: string;
  confidence: ConfidenceLevel;
}

export interface MetaModelPattern {
  client_quote: string;
  pattern_type: string;
  explanation: string;
  coach_question: string;
  confidence: ConfidenceLevel;
}

export interface EmotionalLanguageSignal {
  emotion_or_theme: string;
  client_quote: string;
  severity: SeverityLevel;
  possible_coaching_direction: string;
  confidence: ConfidenceLevel;
}

export interface PartsConflict {
  part_a: string;
  part_b: string;
  evidence_from_transcript: string;
  possible_positive_intentions: string;
  coaching_exploration: string;
  confidence: ConfidenceLevel;
}

export interface ContradictionAndTension {
  tension: string;
  evidence_1: string;
  evidence_2: string;
  possible_meaning: string;
  coach_exploration: string;
  confidence: ConfidenceLevel;
}

export interface IdentityStatement {
  client_quote: string;
  identity_frame: string;
  possible_reframe_direction: string;
  confidence: ConfidenceLevel;
}

export interface ValueAndMotivationClue {
  value_or_motivation: string;
  evidence: string;
  possible_importance: string;
  coach_question: string;
  confidence: ConfidenceLevel;
}

export interface AvoidancePattern {
  pattern: string;
  evidence: string;
  possible_function: string;
  coach_exploration: string;
  confidence: ConfidenceLevel;
}

export interface BreakthroughZone {
  area_of_deepest_leverage: string;
  reasoning: string;
  evidence: string[];
  suggested_next_step: string;
  confidence: ConfidenceLevel;
}

export interface AnalysisConfidence {
  input_type: InputType;
  overall_confidence: ConfidenceLevel;
  limitations: string;
}

export interface SessionAnalysis {
  session_summary: {
    overview: string;
    main_topic: string;
    client_goal: string;
    key_moments: string[];
    agreed_actions: string[];
    follow_up_items: string[];
  };
  key_themes: KeyTheme[];
  next_session_prep: {
    suggested_focus: string;
    questions_to_ask: string[];
    risks_to_avoid: string[];
  };
  limiting_beliefs: LimitingBelief[];
  meta_model_patterns: MetaModelPattern[];
  emotional_language_signals: EmotionalLanguageSignal[];
  parts_conflicts: PartsConflict[];
  contradictions_and_tensions: ContradictionAndTension[];
  identity_statements: IdentityStatement[];
  values_and_motivation_clues: ValueAndMotivationClue[];
  avoidance_or_resistance_patterns: AvoidancePattern[];
  breakthrough_zone: BreakthroughZone;
  suggested_coach_questions: string[];
  suggested_next_session_focus: string;
  important_cautions: string[];
  analysis_confidence: AnalysisConfidence;
}

export interface CoachingSession {
  id: string;
  client_id: string;
  coach_id: string;
  session_date: string;
  duration_minutes: number;
  session_type: string;
  notes: string | null;
  follow_ups: string | null;
  recording_url: string | null;
  session_document_url: string | null;
  session_input_type: 'audio' | 'document' | 'notes_only';
  audio_auto_deleted: boolean;
  consent_confirmed: boolean;
  transcript: string | null;
  session_analysis: SessionAnalysis | null;
  analysis_status: AnalysisStatus;
  created_at: string;
}

export async function getClients(coachId: string): Promise<CoachingClient[]> {
  const { data } = await supabase
    .from('coaching_clients')
    .select('*')
    .eq('coach_id', coachId)
    .order('full_name');
  return data || [];
}

export async function getClient(clientId: string): Promise<CoachingClient | null> {
  const { data } = await supabase
    .from('coaching_clients')
    .select('*')
    .eq('id', clientId)
    .maybeSingle();
  return data;
}

export async function createClient(client: {
  coach_id: string;
  full_name: string;
  email: string;
  phone?: string;
  user_id?: string;
  goals?: string;
  notes?: string;
}) {
  return supabase.from('coaching_clients').insert(client).select().single();
}

export async function updateClient(clientId: string, data: Partial<CoachingClient>) {
  return supabase.from('coaching_clients').update(data).eq('id', clientId);
}

export async function deleteClient(clientId: string) {
  return supabase.from('coaching_clients').delete().eq('id', clientId);
}

export async function getSessions(coachId: string, clientId?: string): Promise<CoachingSession[]> {
  let query = supabase
    .from('coaching_sessions')
    .select('*')
    .eq('coach_id', coachId)
    .order('session_date', { ascending: false });

  if (clientId) {
    query = query.eq('client_id', clientId);
  }

  const { data } = await query;
  return ((data || []) as unknown as Record<string, unknown>[]).map(r => ({
    ...(r as unknown as CoachingSession),
    recording_url: (r.recording_url as string | null) ?? null,
    session_document_url: (r.session_document_url as string | null) ?? null,
    session_input_type: ((r.session_input_type as string) ?? 'notes_only') as CoachingSession['session_input_type'],
    audio_auto_deleted: (r.audio_auto_deleted as boolean) ?? false,
    consent_confirmed: (r.consent_confirmed as boolean) ?? false,
    transcript: (r.transcript as string | null) ?? null,
    session_analysis: (r.session_analysis as SessionAnalysis | null) ?? null,
    analysis_status: ((r.analysis_status as string) ?? 'idle') as AnalysisStatus,
  })) as CoachingSession[];
}

export async function createSession(session: {
  client_id: string;
  coach_id: string;
  session_date: string;
  duration_minutes: number;
  session_type: string;
  notes?: string;
  follow_ups?: string;
}) {
  return supabase.from('coaching_sessions').insert(session).select().single();
}

export async function updateSession(sessionId: string, data: Partial<CoachingSession>) {
  return supabase.from('coaching_sessions').update(data as any).eq('id', sessionId);
}

export async function deleteSession(sessionId: string) {
  return supabase.from('coaching_sessions').delete().eq('id', sessionId);
}
